/**
 * useKeyboardNavigation Hook
 * Grid-based navigation for keyboard control within a zone
 */

import { useState, useCallback, useEffect } from 'react';

interface GridPosition {
  row: number;
  col: number;
}

interface UseKeyboardNavigationOptions {
  /** Number of columns in the grid */
  gridColumns: number;
  /** Total number of items */
  itemCount: number;
  /** Whether navigation is enabled */
  enabled?: boolean;
  /** Callback when an item is selected (Enter key) */
  onSelect?: (index: number) => void;
  /** Callback when back is pressed (Escape key) */
  onBack?: () => void;
  /** Allow navigating up from first row to trigger special action */
  allowUpToExtra?: boolean;
  /** Callback when navigating up from first row */
  onUpFromFirst?: () => void;
  /** Initial focused index */
  initialIndex?: number;
  /** P2.8-4b: D-Pad boundary routing — called when navigating left at left boundary */
  onLeftBoundary?: () => void;
  /** P2.8-4b: D-Pad boundary routing — called when navigating right at right boundary */
  onRightBoundary?: () => void;
  /** P2.8-4b: D-Pad boundary routing — called when navigating up at top boundary */
  onUpBoundary?: () => void;
  /** P2.8-4b: D-Pad boundary routing — called when navigating down at bottom boundary */
  onDownBoundary?: () => void;
}

interface UseKeyboardNavigationReturn {
  focusedIndex: number;
  setFocusedIndex: (index: number) => void;
  gridPosition: GridPosition;
  isFocused: (index: number) => boolean;
  resetFocus: () => void;
}

export function useKeyboardNavigation({
  gridColumns,
  itemCount,
  enabled = true,
  onSelect,
  onBack,
  allowUpToExtra = false,
  onUpFromFirst,
  initialIndex = 0,
  onLeftBoundary,
  onRightBoundary,
  onUpBoundary,
  onDownBoundary,
}: UseKeyboardNavigationOptions): UseKeyboardNavigationReturn {
  const [focusedIndex, setFocusedIndex] = useState(initialIndex);

  const gridPosition: GridPosition = {
    row: Math.floor(focusedIndex / gridColumns),
    col: focusedIndex % gridColumns,
  };

  const totalRows = Math.ceil(itemCount / gridColumns);

  const isFocused = useCallback(
    (index: number) => focusedIndex === index,
    [focusedIndex]
  );

  const resetFocus = useCallback(() => {
    setFocusedIndex(initialIndex);
  }, [initialIndex]);

  // Handle navigation
  const navigate = useCallback(
    (direction: 'up' | 'down' | 'left' | 'right') => {
      if (!enabled || itemCount === 0) return;

      const { row, col } = gridPosition;

      switch (direction) {
        case 'up': {
          if (row > 0) {
            const newIndex = Math.min((row - 1) * gridColumns + col, itemCount - 1);
            setFocusedIndex(newIndex);
          } else if (allowUpToExtra && onUpFromFirst) {
            onUpFromFirst();
          } else {
            onUpBoundary?.();
          }
          break;
        }
        case 'down': {
          if (row < totalRows - 1) {
            const newIndex = Math.min((row + 1) * gridColumns + col, itemCount - 1);
            setFocusedIndex(newIndex);
          } else {
            onDownBoundary?.();
          }
          break;
        }
        case 'left': {
          if (gridColumns === 1) { onLeftBoundary?.(); break; }
          if (focusedIndex > 0) {
            setFocusedIndex(focusedIndex - 1);
          } else {
            onLeftBoundary?.();
          }
          break;
        }
        case 'right': {
          if (gridColumns === 1) { onRightBoundary?.(); break; }
          if (focusedIndex < itemCount - 1) {
            setFocusedIndex(focusedIndex + 1);
          } else {
            onRightBoundary?.();
          }
          break;
        }
      }
    },
    [
      enabled,
      itemCount,
      gridPosition,
      gridColumns,
      totalRows,
      focusedIndex,
      allowUpToExtra,
      onUpFromFirst,
      onLeftBoundary,
      onRightBoundary,
      onUpBoundary,
      onDownBoundary,
    ]
  );

  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if event was already handled
      if (e.defaultPrevented) return;

      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          navigate('up');
          break;
        case 'ArrowDown':
          e.preventDefault();
          navigate('down');
          break;
        case 'ArrowLeft':
          if (gridColumns > 1) {
            e.preventDefault();
            navigate('left');
          }
          break;
        case 'ArrowRight':
          if (gridColumns > 1) {
            e.preventDefault();
            navigate('right');
          }
          break;
        case 'Enter':
          e.preventDefault();
          if (onSelect) {
            onSelect(focusedIndex);
          }
          break;
        case 'Escape':
          e.preventDefault();
          if (onBack) {
            onBack();
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enabled, navigate, focusedIndex, onSelect, onBack]);

  return {
    focusedIndex,
    setFocusedIndex,
    gridPosition,
    isFocused,
    resetFocus,
  };
}
